import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import SingleSelect from '../../../../../../components/SingleSelect';
import HttpStatus from '../../../../../../enums/httpStatus';
import { parametersApi } from '../../../../../../services/requests';
import { Parameter } from '../../../../../../types/apiResponse/Parameters';
import * as s from './styles';
import AddBtn from '../../../../../../components/Buttons/AddBtn';

type Props = {
  showModal: boolean;
  closeModal: () => void;
  callback: () => void;
  parameterSelected: Parameter;
};

export const ChangeParameterValue = ({
  showModal,
  closeModal,
  callback,
  parameterSelected,
}: Props) => {
  const { t } = useTranslation();

  const [value, setValue] = useState<any>();
  // Para tipo array
  const [arrayValue, setArrayValue] = useState<string[]>([]);
  const [newArrayItem, setNewArrayItem] = useState('');

  useEffect(() => {
    if (parameterSelected.value_type === 'boolean') {
      setValue(parameterSelected.boolean_value);
    } else if (parameterSelected.value_type === 'array') {
      try {
        if (typeof parameterSelected.valor === 'string') {
          const arr = JSON.parse(parameterSelected.valor);
          if (Array.isArray(arr)) setArrayValue(arr);
          else setArrayValue([]);
        } else {
          setArrayValue([]);
        }
      } catch {
        setArrayValue([]);
      }
    } else {
      setValue(parameterSelected.valor);
    }
  }, [parameterSelected]);

  const [waitingSaveRequest, setWaitingSaveRequest] = useState(false);

  const booleanSelectOptions = [
    { value: true, label: t('yes') },
    { value: false, label: t('no') },
  ];

  const handleInputType = () => {
    const numberTypes = ['numeric', 'number', 'double', 'float', 'int', 'integer'];

    if (numberTypes.includes(parameterSelected.value_type)) {
      return 'number';
    }

    return 'text';
  };

  const saveBtnAction = async () => {
    setWaitingSaveRequest(true);

    let valueToReq: any = value;
    if (parameterSelected.value_type === 'array') {
      valueToReq = JSON.stringify(arrayValue);
    } else if (handleInputType() === 'number') {
      if (value === '') {
        frontendNotification({
          message: t('enterNumber'),
          type: 'error',
        });
        setWaitingSaveRequest(false);
        return;
      }
      valueToReq = Number(value);
    }

    try {
      parametersApi.query = '';
      const response = await parametersApi.update(
        { value: valueToReq },
        parameterSelected?.parametro as string,
      );

      if (response.status === HttpStatus.CREATED || response.status === HttpStatus.OK) {
        frontendNotification({
          message: t('success'),
          type: 'success',
        });

        callback();
        setValue(undefined);
        setArrayValue([]);
        closeModal();
      } else {
        throw response;
      }

      setWaitingSaveRequest(false);
    } catch (err) {
      if (!parametersApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
      setWaitingSaveRequest(false);
    }
  };

  const allowedValuesToOptions = () => {
    if (parameterSelected.allowedValues) {
      return parameterSelected.allowedValues.map((v) => ({
        label: v,
        value: v,
      }));
    }
    return [];
  };

  return (
    <Modal
      title={parameterSelected.parametro as string}
      showModal={showModal}
      closeModal={closeModal}
      action={() => saveBtnAction()}
      isLoading={waitingSaveRequest}
      style={{ minWidth: '350px' }}
    >
      <s.Row>
        <s.Item>
          <b>{t('description')}:</b> {parameterSelected?.descricao}
        </s.Item>
      </s.Row>

      {parameterSelected.value_type === 'array' ? (
        <>
          <s.Row>
            <s.Item>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="input"
                  value={newArrayItem}
                  onChange={(e: any) => setNewArrayItem(e.target.value)}
                  placeholder={t('addValue') as string}
                  disabled={waitingSaveRequest}
                  style={{ flex: 1 }}
                  onKeyDown={(e: any) => {
                    if (e.key === 'Enter' && newArrayItem.trim()) {
                      handleAddArrayItem();
                    }
                  }}
                />
                {/* <button
                  type="button"
                  style={{ padding: '0 12px' }}
                  disabled={waitingSaveRequest || !newArrayItem.trim()}
                  onClick={handleAddArrayItem}
                >
                  +
                </button> */}
                <AddBtn
                  onClick={handleAddArrayItem}
                  disabled={waitingSaveRequest || !newArrayItem.trim()}
                />
              </div>
            </s.Item>
          </s.Row>
          {arrayValue.length > 0 && (
            <s.Row>
              <s.Item>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {arrayValue.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        className="input"
                        value={item}
                        onChange={(e: any) => handleEditArrayItem(idx, e.target.value)}
                        disabled={waitingSaveRequest}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        // eslint-disable-next-line max-len
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d00', fontSize: 18 }}
                        onClick={() => handleRemoveArrayItem(idx)}
                        title={t('remove') as string}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </s.Item>
            </s.Row>
          )}
        </>
      ) : parameterSelected.allowedValues ? (
        <s.Row>
          <s.Item>
            <SingleSelect
              options={allowedValuesToOptions()}
              style={{ minWidth: '100%' }}
              value={{ value, label: value }}
              onChange={(v: any) => setValue(v.value)}
            />
          </s.Item>
        </s.Row>
      ) : (
        <>
          {parameterSelected.value_type === 'boolean' ? (
            <s.Row>
              <s.Item>
                <SingleSelect
                  options={booleanSelectOptions}
                  style={{ minWidth: '100%' }}
                  value={value ? booleanSelectOptions[0] : booleanSelectOptions[1]}
                  onChange={(v: any) => setValue(v.value)}
                />
              </s.Item>
            </s.Row>
          ) : (
            <div className="row">
              <div className="inputWithLabel">
                <div className="label">
                  {handleInputType() === 'number' ? `${t('value')} (${t('number')})` : t('value')}
                </div>
                <input
                  className="input"
                  value={value}
                  onChange={(e: any) => setValue(e.target.value)}
                  disabled={waitingSaveRequest}
                  type={handleInputType()}
                  min={
                    handleInputType() === 'number'
                      ? parameterSelected.allowedValuesMinSize
                      : undefined
                  }
                  max={
                    handleInputType() === 'number'
                      ? parameterSelected.allowedValuesMaxSize
                      : undefined
                  }
                />
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
  // Funções para array
  function handleAddArrayItem() {
    if (!newArrayItem.trim()) return;
    setArrayValue([...arrayValue, newArrayItem.trim()]);
    setNewArrayItem('');
  }

  function handleEditArrayItem(idx: number, newValue: string) {
    const arr = [...arrayValue];
    arr[idx] = newValue;
    setArrayValue(arr);
  }

  function handleRemoveArrayItem(idx: number) {
    setArrayValue(arrayValue.filter((_, i) => i !== idx));
  }
};

export default ChangeParameterValue;
